/**
 * <letter-customizer>
 *
 * Letter-customization preview for the product page. Renders the typed
 * letters in the custom font with a decorative ornament underneath that
 * snaps to the pattern whose natural width best matches the rendered text.
 * Two ornament styles are switchable by the customer. The business-day
 * delivery estimate is rendered server-side (Liquid), not here.
 *
 * The current text and chosen style are mirrored onto hidden inputs so the
 * Vue product `add()` method can submit them as line item properties.
 */
(function () {
  if (customElements.get('letter-customizer')) return;

  var LETTERS_ONLY = /[^A-Za-z]/g;

  // How much wider than the text the ornament may be before it is shrunk to fit.
  // Keeps a little flourish overhang; beyond this, short names get a proportionally
  // smaller ornament instead of an oversized one.
  var ORNAMENT_WIDTH_RATIO = 1.15;

  // The ornament stops shrinking once it is this wide (px), so very short names still
  // get a legible flourish instead of a tiny one.
  var MIN_ORNAMENT_WIDTH = 55;

  class LetterCustomizer extends HTMLElement {
    connectedCallback() {
      // Guard against double init on Shopify section reloads.
      if (this._ready) return;
      this._ready = true;
      this._controller = new AbortController();

      this.refs = {};
      this.querySelectorAll('[data-ref]').forEach(function (el) {
        var key = el.getAttribute('data-ref');
        if (key === 'styleBtn') {
          (this.refs.styleBtns = this.refs.styleBtns || []).push(el);
        } else {
          this.refs[key] = el;
        }
      }, this);

      this.minLength = parseInt(this.dataset.minLength, 10) || 1;
      this.maxLength = parseInt(this.dataset.maxLength, 10) || 20;
      this.userScale = parseFloat(this.dataset.scale) || 1;
      this.currentStyle = this.dataset.defaultStyle === 'style2' ? 'style2' : 'style1';
      // Optional: product option name (e.g. "_style") whose value carries the SKU per style.
      this.variantOption = (this.dataset.variantOption || '').trim();

      this._parsePatterns();
      this._measureOrnaments();
      this._bindEvents();
      this._initStyleFromVariant();
      this._syncStyleButtons();

      // The two styles use different, lazily-loaded fonts; wait for the active one before
      // measuring so the ornament width is based on real glyphs, not a fallback font.
      this._recomputeForFont();
      // Render once immediately so the field is usable before the font settles.
      this.render();
    }

    disconnectedCallback() {
      this._ready = false;
      if (this._controller) this._controller.abort();
    }

    _parsePatterns() {
      // Path data is loaded from the separate letter-customizer-patterns.js asset.
      // Deep clone the shared data so per-instance bbox measurements stay isolated.
      var data = window.LetterCustomizerPatterns || { style1: [], style2: [] };
      this.patterns = JSON.parse(JSON.stringify(data));
    }

    /** Measure each path's natural bounding box once, then sort each ladder by width. */
    _measureOrnaments() {
      var ns = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('width', '0');
      svg.setAttribute('height', '0');
      svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;visibility:hidden';
      document.body.appendChild(svg);

      ['style1', 'style2'].forEach(function (group) {
        (this.patterns[group] || []).forEach(function (p) {
          var path = document.createElementNS(ns, 'path');
          path.setAttribute('d', p.d);
          svg.appendChild(path);
          var b = path.getBBox();
          p.bbox = { x: b.x, y: b.y, w: b.width, h: b.height };
          svg.removeChild(path);
        });
        this.patterns[group].sort(function (a, b) { return a.bbox.w - b.bbox.w; });
      }, this);

      document.body.removeChild(svg);

      // Widest natural width across both groups, used to derive the scale.
      this.maxNativeWidth = 0;
      ['style1', 'style2'].forEach(function (group) {
        (this.patterns[group] || []).forEach(function (p) {
          if (p.bbox.w > this.maxNativeWidth) this.maxNativeWidth = p.bbox.w;
        }, this);
      }, this);
    }

    _bindEvents() {
      var signal = this._controller.signal;
      var self = this;

      this.refs.input.addEventListener('input', function () {
        var cleaned = self.refs.input.value.replace(LETTERS_ONLY, '');
        if (cleaned !== self.refs.input.value) self.refs.input.value = cleaned;
        self.render();
      }, { signal: signal });

      (this.refs.styleBtns || []).forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.currentStyle = btn.dataset.style === 'style2' ? 'style2' : 'style1';
          self._syncStyleButtons();
          // When the style is backed by a product variant, switch it so the order carries
          // the right SKU (drives the hidden system option in the native variant switcher).
          self._selectStyleVariant();
          // Font changes with the style; re-measure once the new font is loaded so the
          // ornament width is recomputed against the real glyph widths.
          self._recomputeForFont();
        }, { signal: signal });
      });

      // Inscription guide popup (native <dialog>: built-in focus trap + Esc-to-close).
      if (this.refs.guideOpen && this.refs.guideDialog) {
        this.refs.guideOpen.addEventListener('click', function () {
          var dlg = self.refs.guideDialog;
          if (typeof dlg.showModal === 'function') dlg.showModal();
          else dlg.setAttribute('open', '');
        }, { signal: signal });
      }
      if (this.refs.guideClose && this.refs.guideDialog) {
        this.refs.guideClose.addEventListener('click', function () {
          var dlg = self.refs.guideDialog;
          if (typeof dlg.close === 'function') dlg.close();
          else dlg.removeAttribute('open');
        }, { signal: signal });
      }
    }

    _syncStyleButtons() {
      (this.refs.styleBtns || []).forEach(function (btn) {
        // aria-pressed is the single source of truth; CSS styles the selected button.
        btn.setAttribute('aria-pressed', btn.dataset.style === this.currentStyle ? 'true' : 'false');
      }, this);
      // Drives the preview --letter-custom-font-family swap in CSS.
      this.dataset.currentStyle = this.currentStyle;
    }

    /* ---- Style <-> product variant, so the order carries the per-style SKU ---- */

    _productScope() {
      return this.closest('section.product') || this.closest('.product-details') || document;
    }

    _buttonForStyle(style) {
      var btns = this.refs.styleBtns || [];
      for (var i = 0; i < btns.length; i++) {
        var s = btns[i].dataset.style === 'style2' ? 'style2' : 'style1';
        if (s === style) return btns[i];
      }
      return null;
    }

    _styleForVariantValue(value) {
      var btns = this.refs.styleBtns || [];
      for (var i = 0; i < btns.length; i++) {
        if (btns[i].dataset.variantValue === value) {
          return btns[i].dataset.style === 'style2' ? 'style2' : 'style1';
        }
      }
      return null;
    }

    /** The hidden <a> proxies that product-variant-switcher-generic renders for our option. */
    _systemOptionContainer() {
      if (!this.variantOption) return null;
      var nodes = this._productScope().querySelectorAll('[data-system-option]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].getAttribute('data-system-option') === this.variantOption) return nodes[i];
      }
      return null;
    }

    /** On load, adopt the selected variant's style value so the buttons match the variant. */
    _initStyleFromVariant() {
      if (!this.variantOption) return;
      if (!(window._erj_ && window._erj_.getProductInfo)) return;
      var info = window._erj_.getProductInfo(this._productScope());
      if (!info || !info.options || !info.variants) return;
      var pos = 0;
      for (var i = 0; i < info.options.length; i++) {
        if (info.options[i].name === this.variantOption) { pos = info.options[i].position; break; }
      }
      if (!pos) return;
      var selected = null;
      for (var j = 0; j < info.variants.length; j++) {
        if (String(info.variants[j].id) === String(info.selectedVariantId)) { selected = info.variants[j]; break; }
      }
      if (!selected) return;
      var style = this._styleForVariantValue(selected['option' + pos]);
      if (style) this.currentStyle = style;
    }

    /** Proxy-click the hidden system variant option matching the current style. */
    _selectStyleVariant() {
      var container = this._systemOptionContainer();
      var btn = this._buttonForStyle(this.currentStyle);
      var value = btn && btn.dataset.variantValue;
      if (!container || !value) return;
      var links = container.querySelectorAll('a[data-value]');
      for (var i = 0; i < links.length; i++) {
        if (links[i].getAttribute('data-value') === value) { links[i].click(); return; }
      }
    }

    /**
     * Ensure the active style's font (loaded lazily, only when first used) is ready, then
     * recompute the reference scale and re-render. Until it resolves the browser measures
     * with a fallback font, which would size the ornament wrong.
     */
    _recomputeForFont() {
      var self = this;
      var apply = function () { self._computeBaseScale(); self.render(); };
      var family = this.currentStyle === 'style2' ? 'letter-custom-2' : 'letter-custom-1';
      if (document.fonts && document.fonts.load) {
        // Size is irrelevant to loading; any valid font shorthand triggers the download.
        document.fonts.load('64px "' + family + '"').then(apply, apply);
      } else {
        apply();
      }
    }

    /** Scale (SVG units -> px) so the widest ornament maps onto a max-length string. */
    _computeBaseScale() {
      if (!this.maxNativeWidth) { this.baseScale = 1; return; }
      var sample = new Array(this.maxLength + 1).join('a');
      var refWidth = this._measureText(sample) || (this.maxLength * 30);
      this.baseScale = refWidth / this.maxNativeWidth;
    }

    _measureText(str) {
      var preview = this.refs.preview;
      var prev = preview.textContent;
      preview.textContent = str || '';
      var w = preview.getBoundingClientRect().width;
      preview.textContent = prev;
      return w;
    }

    /** Pick the ornament whose scaled natural width is closest to the text width. */
    _pickPattern(textWidth) {
      var list = this.patterns[this.currentStyle] || [];
      if (!list.length) return null;
      var naturalScale = (this.baseScale || 1) * this.userScale;
      var best = list[0], bestDelta = Infinity;
      for (var i = 0; i < list.length; i++) {
        var delta = Math.abs(list[i].bbox.w * naturalScale - textWidth);
        if (delta < bestDelta) { bestDelta = delta; best = list[i]; }
      }
      // Short text: even the narrowest pattern is much wider than the letters. Shrink the
      // whole ornament uniformly — one scale for both width and height, so the aspect ratio
      // is preserved — so it never exceeds ORNAMENT_WIDTH_RATIO × the text width. Capped at
      // the natural scale, so medium/long text keeps its current size. userScale still applies.
      var fitScale = (textWidth * ORNAMENT_WIDTH_RATIO / best.bbox.w) * this.userScale;
      // Floor: stop shrinking once the ornament is MIN_ORNAMENT_WIDTH px wide (still uniform,
      // so the aspect ratio holds). Never scale past the natural pick.
      var minScale = MIN_ORNAMENT_WIDTH / best.bbox.w;
      var scale = Math.min(naturalScale, Math.max(fitScale, minScale));
      return { pattern: best, scale: scale };
    }

    render() {
      var text = this.refs.input.value;
      this.refs.preview.textContent = text;

      // Live character count (maxlength on the input enforces the ceiling).
      if (this.refs.counter) {
        this.refs.counter.textContent = text.length + ' / ' + this.maxLength;
      }

      // Ornament
      if (text.length) {
        var tw = this.refs.preview.getBoundingClientRect().width;
        var chosen = this._pickPattern(tw);
        if (chosen) {
          var b = chosen.pattern.bbox;
          var w = b.w * chosen.scale;
          var h = b.h * chosen.scale;
          this.refs.ornament.innerHTML =
            '<svg width="' + w.toFixed(1) + '" height="' + h.toFixed(1) + '" ' +
            'viewBox="' + b.x + ' ' + b.y + ' ' + b.w + ' ' + b.h + '" ' +
            'role="presentation" focusable="false">' +
            '<path d="' + chosen.pattern.d + '" fill="currentColor"/></svg>';
        }
      } else {
        this.refs.ornament.innerHTML = '';
      }

      this._updateValidity(text);
    }

    _updateValidity(text) {
      var valid = text.length >= this.minLength && text.length <= this.maxLength;
      this.classList.toggle('is-invalid', !valid && text.length > 0);

      // Mirror the text onto the hidden input for the cart hook. The style is carried by
      // the product variant now, so it is no longer submitted as a line item property.
      this.refs.propText.value = valid ? text : '';

      if (this.refs.hint) {
        this.refs.hint.textContent = (!valid && text.length > 0) ? this._invalidMessage() : '';
      }
      this._valid = valid;
      this._toggleBuyNow(valid);
    }

    /**
     * The native buy-now / express checkout submits straight to checkout and never calls
     * getCartProperties, so it must be blocked here. Disable it while the inscription is
     * invalid (the Add-to-Bag button is already gated in its own click handler).
     */
    _toggleBuyNow(valid) {
      var nodes = this._productScope().querySelectorAll('.hvg-express-checkout-buttons');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].classList.toggle('letter-customizer-disabled', !valid);
      }
    }

    _invalidMessage() {
      // min/max are already interpolated by Liquid via the `t` filter.
      return this.dataset.invalidMessage || '';
    }

    /* ---- Public API for the Vue product add() hook ---- */

    /** @returns {{valid:boolean, properties:Object}} */
    getCartProperties() {
      var props = {};
      if (this._valid) {
        props[this.refs.propText.dataset.propKey] = this.refs.propText.value;
        // Estimated delivery window (static, from the block settings).
        if (this.refs.propDelivery && this.refs.propDelivery.dataset.propKey) {
          props[this.refs.propDelivery.dataset.propKey] = this.refs.propDelivery.value;
        }
      }
      return { valid: !!this._valid, properties: props };
    }

    /** Surface the validation error and move focus to the input. */
    reportInvalid() {
      this.classList.add('is-invalid');
      if (this.refs.hint) this.refs.hint.textContent = this._invalidMessage();
      this.refs.input.focus();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('letter-customizer', LetterCustomizer);
})();
